// A machine's workbench (RFC-057), the pane /s/{id}/workbench draws and the exam
// edition's room (RFC-062) — its work tree's files
// on the left, the file open in the middle with the machine's own terminal
// under it, and the thread with its agent (byj acp, in the machine) at the
// side. Everything goes through the relay to the machine's sidecar; the
// relay lets in only whoever may type into the machine (D4).
//
// A save names the version the editor read and is refused if the machine
// has the file otherwise since — the agent wrote it — and the page says so,
// to reload or keep its own (D7). After an edit of the agent's the open
// files are looked at again: one unchanged in the editor is read again, one
// changed in both is marked. A reload finds the thread where it was: the
// latest session in the work tree is loaded again.
//
// On a narrow screen (where the side would stack under the page) the four
// panes are tabs, one filling the screen at a time: Files, Editor,
// Terminal and the agent. The page is the same tree, its panes hidden
// rather than taken away, so the terminal's connection, the editor's undo
// and a half-written prompt outlive a switch or a turned tablet.
import { Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { lazyChunk } from "../chunk";
import { keep, readKept, useKept, useKeptFlag } from "../kept";
import { api, wsURL, type ExamSitting, type ShareDetail, type User } from "../api";
import type { SocketLike } from "../live";
import { useMedia } from "../media";
import { navigate } from "../router";
import { t } from "../i18n";
import { useConfirm } from "./Confirm";
import { useEditTitle } from "./EditTitle";
import { FileIcon } from "./FileIcon";
import { FileTree } from "./FileTree";
import { AgentThread } from "./AgentThread";
import { ExamBar, ExamLobby, ExamNotice, ExamQuestionPanel, ExamQuestions, examSpan } from "./ExamRoom";
import { ShareTimeline } from "./ShareTimeline";
import { SplitPane } from "./SplitPane";
import { panelId, tabId, Tabs } from "./Tabs";
import { VSplit } from "./VSplit";
import {
  agentLabel,
  Codes,
  dirty,
  emptyThread,
  parseReceipt,
  type HandIn,
  examLeft,
  examWithSitting,
  examSkew,
  reduceThread,
  RpcError,
  WorkbenchConnection,
  type Entry,
  type ExamQuestion,
  type ExamState,
  type FileText,
  type OpenFile,
  type PastThread,
  type PermissionItem,
  type Project,
  type WorkbenchInfo,
  type WorkbenchState,
} from "../workbench";

const Screen = lazyChunk(() => import("./Screen"));
const CodeEditor = lazyChunk(() => import("./CodeEditor"));

/** The longest change not saved that is kept with a file across visits: a browser's storage is a few megabytes in all. */
const maxKeptDraft = 256 * 1024;

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

type Loaded = OpenFile & { loadedAt: string };

/** The workbench's panes, tabs of their own on a narrow screen. */
type Pane = "files" | "editor" | "terminal" | "agent";

/** Narrow: where the side stacks under the page (SplitPane's breakpoint), the panes are tabs. */
export const NarrowWorkbench = "(max-width: 900px)";

export function WorkbenchView({
  share,
  me,
  socket,
  setTimer,
}: {
  share: ShareDetail;
  me: User;
  /** A socket factory, for tests. */
  socket?: (url: string) => SocketLike;
  setTimer?: (fn: () => void, ms: number) => unknown;
}) {
  const [conn, setConn] = useState<WorkbenchState>({ kind: "connecting" });
  const [sidecar, setSidecar] = useState(false);
  const [info, setInfo] = useState<WorkbenchInfo | null>(null);
  // each agent's state, by name, as workbench/info and workbench/agent say it
  const [agents, setAgents] = useState<Record<string, { state: string; error?: string }>>({ byj: { state: "idle" } });
  const setAgentState = (name: string, st: { state: string; error?: string }) => setAgents((a) => ({ ...a, [name]: st }));
  const [thread, dispatch] = useReducer(reduceThread, emptyThread);
  const [files, setFiles] = useState<Loaded[]>([]);
  const [active, setActive] = useState<string | undefined>();
  const [refresh, setRefresh] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // the thread is found where it was before anything is asked: a prompt
  // sent meanwhile would be wiped by the session loaded
  const [restored, setRestored] = useState(false);
  // the folder the thread works in, kept per share: the work tree, or an
  // exam's question, whose workspace a thread must be in to count (D8)
  const [project, keepProject] = useKept(`share.workbench.project.${share.id}`, "", (v) => v, (v) => v);
  const [projects, setProjects] = useState<Project[]>([]);
  // the agent the thread is with (RFC-057 D5), kept per share; an exam's
  // question is the agent its attempt is sat with, whatever was chosen
  const [chosenAgent, keepAgent] = useKept(`share.workbench.agent.${share.id}`, "byj", (v) => v, (v) => v);
  const exam = projects.find((x) => x.path === project);
  // the agents ACP has been initialized with on this connection
  const initialized = useRef(new Set<string>());
  const [ask, askDialog] = useEditTitle();
  const [confirm, confirmDialog] = useConfirm();
  const wb = useRef<WorkbenchConnection | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;
  const threadRef = useRef(thread);
  threadRef.current = thread;
  // the exam room (RFC-058): the sitting as the machine's byj says it, how
  // far the relay's clock is ahead of this browser's, and the question shown
  const [examSt, setExamSt] = useState<ExamState | null>(null);
  const [skew, setSkew] = useState(0);
  const [tick, setTick] = useState(() => Date.now());
  const [examBusy, setExamBusy] = useState<"prepare" | "check" | "begin" | "submit" | undefined>();
  const [agentCheck, setAgentCheck] = useState<string | undefined>();
  const [lobbyAgent, setLobbyAgent] = useState("");
  // each question's last hand-in as the machine told it, and when: kept in
  // this browser, so a reload still shows what was handed in
  const [receipts, keepReceipts] = useKept<Record<string, HandIn>>(
    `share.workbench.receipts.${share.id}`,
    {},
    (s) => {
      try {
        const v = JSON.parse(s) as unknown;
        return v && typeof v === "object" ? (v as Record<string, HandIn>) : undefined;
      } catch {
        return undefined; // what an older page wrote: nothing kept
      }
    },
    (v) => JSON.stringify(v),
  );
  const [examQ, keepExamQ] = useKept(`share.workbench.examq.${share.id}`, "", (v) => v, (v) => v);
  // the agent the thread is with: a sitting's begun is the one it is sat
  // with, fixed for the whole room (RFC-058: one sitting, one agent), whatever folder is picked;
  // else an exam question's, else the one chosen here
  const sittingAgent = info?.exam && examSt?.begun ? examSt.agent : undefined;
  const agentName = sittingAgent ?? exam?.agent ?? (agents[chosenAgent] ? chosenAgent : "byj");
  const agentLocked = sittingAgent !== undefined || exam?.agent !== undefined;
  const agentRef = useRef(agentName);
  agentRef.current = agentName;
  // a narrow screen's pane, the files first; a file opened shows the editor
  const narrow = useMedia(NarrowWorkbench, false);
  const [pane, setPane] = useState<Pane>("files");
  // a wide screen's panes, each folded or not from the head's toggles or
  // its own edge, kept per browser: the files and the agent at the sides,
  // the terminal under the editor
  const [filesFolded, foldFiles] = useKeptFlag("share.workbench.files.collapsed", false);
  const [agentFolded, foldAgent] = useKeptFlag("share.workbench.side.collapsed", false);
  const [terminalHidden, hideTerminal] = useKeptFlag("share.workbench.terminal.hidden", false);
  const promptField = useRef<HTMLTextAreaElement>(null);

  const checkOpen = useCallback(async (paths: string[]) => {
    const c = wb.current;
    if (!c) return;
    const which = filesRef.current.filter((f) => paths.length === 0 || paths.some((p) => f.path === p || f.path.startsWith(p + "/")));
    for (const f of which) {
      try {
        const st = await c.request<{ version?: string }>("fs/stat", { path: f.path });
        if (st.version === undefined || st.version === f.version) continue;
        if (!dirty(f)) {
          const r = await c.request<FileText>("fs/read", { path: f.path });
          setFiles((fs) => fs.map((x) => (x.path === f.path ? { ...x, text: r.text, saved: r.text, version: r.version, conflict: false, gone: false, loadedAt: String(Date.now()) } : x)));
        } else {
          setFiles((fs) => fs.map((x) => (x.path === f.path ? { ...x, conflict: true } : x)));
        }
      } catch (err) {
        if (err instanceof RpcError && err.code === Codes.notFound) setFiles((fs) => fs.map((x) => (x.path === f.path ? { ...x, gone: true } : x)));
      }
    }
  }, []);

  const loadExam = useCallback(async () => {
    const c = wb.current;
    if (!c) return;
    try {
      const st = await c.request<ExamState>("exam/state");
      setExamSt(st);
      setSkew(examSkew(st));
    } catch (err) {
      if (!(err instanceof RpcError && err.code === Codes.closed)) setProblem(errText(err));
    }
  }, []);

  useEffect(() => {
    const c = new WorkbenchConnection({
      url: wsURL(`/v1/shares/${encodeURIComponent(share.id)}/workbench`),
      socket,
      setTimer,
      onState: setConn,
      onNotification: (method, params, from) => {
        const p = (params ?? {}) as Record<string, unknown>;
        const agentOf = typeof p.agent === "string" ? p.agent : (from ?? "byj");
        switch (method) {
          case "workbench/sidecar":
            setSidecar(p.connected === true);
            break;
          case "workbench/agent":
            setAgentState(agentOf, { state: String(p.state ?? "idle"), error: typeof p.error === "string" ? p.error : undefined });
            break;
          case "workbench/changed":
            setRefresh((n) => n + 1);
            void checkOpen(Array.isArray(p.paths) ? (p.paths as string[]) : []);
            break;
          case "exam/changed":
            void loadExam();
            setRefresh((n) => n + 1);
            break;
          case "session/update":
            // the thread's agent's alone: another's sessions are other threads
            if ((from ?? "byj") === agentRef.current) dispatch({ type: "update", params });
            break;
        }
      },
      onRequest: (id, method, params, from) => {
        if (method === "session/request_permission" && (from ?? "byj") === agentRef.current) dispatch({ type: "permission", rpc: id, params });
      },
    });
    wb.current = c;
    c.start();
    return () => {
      wb.current = null;
      c.stop("left");
    };
  }, [share.id, socket, setTimer, checkOpen, loadExam, attempt]);

  // once the machine is there on an open connection: what the workbench
  // is, and the thread where it was
  const ready = conn.kind === "open" && sidecar;
  useEffect(() => {
    const c = wb.current;
    if (!ready || !c) return;
    let gone = false;
    setRestored(false);
    initialized.current = new Set();
    (async () => {
      try {
        const i = await c.request<WorkbenchInfo>("workbench/info");
        if (gone) return;
        setInfo(i);
        const known: Record<string, { state: string; error?: string }> = {};
        for (const a of i.agents ?? (i.agent ? [i.agent] : [])) known[a.name] = { state: a.state, error: a.error };
        setAgents(Object.keys(known).length > 0 ? known : { byj: { state: "idle" } });
        setRefresh((n) => n + 1);
        await initialize(c, agentName);
        // the thread where it was: the one shown before, else the newest, of
        // those the agent has open here — sent again, a running turn and all
        // — else the newest saved one nobody else holds, loaded
        const cwd = cwdOf(i, project);
        const past = await pastThreads(c, cwd, agentName);
        if (gone) return;
        const shown = threadRef.current.sessionId;
        const pick = past.find((x) => x.state === "open" && x.sessionId === shown) ?? past.find((x) => x.state === "open") ?? past.find((x) => x.state === "saved");
        if (!pick) {
          // a folder with no thread to go back to: the next prompt makes one there
          if (shown) dispatch({ type: "reset" });
          return;
        }
        await openThread(pick, cwd, agentName);
      } catch (err) {
        if (!gone && !(err instanceof RpcError && err.code === Codes.closed)) setProblem(errText(err));
      } finally {
        if (!gone) setRestored(true);
      }
    })();
    return () => {
      gone = true;
    };
  }, [ready, project, agentName]);

  // the files open, and which is shown, kept per share in the browser and
  // opened again on coming back — a change not saved with them, the
  // version it was made on, and marked when the machine's file has moved
  // on since
  const filesKey = `share.workbench.files.${share.id}`;
  const reopened = useRef(false);
  useEffect(() => {
    if (!reopened.current) return; // not until those kept are open again
    const open = files.map((f) => (dirty(f) && f.text.length <= maxKeptDraft ? { path: f.path, draft: f.text, version: f.version } : { path: f.path }));
    keep(filesKey, JSON.stringify({ open, active }));
  }, [files, active, filesKey]);
  useEffect(() => {
    const c = wb.current;
    if (!ready || !info || !c || reopened.current) return;
    let kept: { open?: { path: string; draft?: string; version?: string }[]; active?: string } = {};
    try {
      kept = JSON.parse(readKept(filesKey) ?? "{}");
    } catch {
      kept = {};
    }
    (async () => {
      const back: Loaded[] = [];
      for (const k of kept.open ?? []) {
        try {
          const r = await c.request<FileText>("fs/read", { path: k.path });
          back.push({
            path: k.path,
            text: k.draft ?? r.text,
            saved: r.text,
            version: r.version,
            conflict: k.draft !== undefined && k.version !== r.version,
            loadedAt: String(Date.now()),
          });
        } catch {
          // gone, or no longer text: not opened again
        }
      }
      setFiles((fs) => [...back.filter((b) => !fs.some((f) => f.path === b.path)), ...fs]);
      setActive((a) => a ?? (back.some((b) => b.path === kept.active) ? kept.active : back[0]?.path));
      reopened.current = true;
    })();
  }, [ready, info, filesKey]);

  // the exam's questions in the tree, looked for again whenever it may have changed
  useEffect(() => {
    if (!ready) return;
    wb.current?.request<{ projects: Project[] }>("workbench/projects").then(
      (r) => setProjects(r.projects ?? []),
      () => {},
    );
  }, [ready, refresh]);

  const disabled = !ready;

  // the exam room (RFC-058): its sitting read as the machine is there, every
  // 30 s and whenever the machine says it changed; the clock ticking while
  // it runs, on the relay's time
  const machineRoom = info?.exam ? examSt : null;
  // when the assignment's window closes, the relay's word, for the lobby's
  // times: a sitting ends then at the latest. Not known, the lobby says less
  const [closesAt, setClosesAt] = useState<string | undefined>();
  // where the candidate goes back to once done, when their exam names one (RFC-061)
  const [returnURL, setReturnURL] = useState<string | undefined>();
  // the sitting as the relay keeps it: the candidate's own end and the
  // window's close as they are now, which the machine read only at Begin.
  // Read again every 30 s while it is begun and not closed, the machine
  // there or not: an operator's end now stops it (byj-1wv.118.19)
  const [relaySitting, setRelaySitting] = useState<ExamSitting | null>(null);
  const roomAssignment = machineRoom?.assignment;
  const relayWatch = machineRoom?.begun === true && relaySitting?.closed !== true;
  useEffect(() => {
    if (!roomAssignment) return;
    let gone = false;
    const read = () =>
      api.examAssignment(roomAssignment).then(
        (p) => {
          if (gone) return;
          setClosesAt(p.assignment.closes_at);
          setReturnURL(p.assignment.return_url);
          setRelaySitting(p.sitting ?? null);
        },
        () => {},
      );
    void read();
    const id = relayWatch ? setInterval(() => void read(), 30_000) : undefined;
    return () => {
      gone = true;
      clearInterval(id);
    };
  }, [roomAssignment, relayWatch]);
  const room = machineRoom ? examWithSitting(machineRoom, relaySitting) : null;
  // the file tree in an exam's room (byj-1wv.118.20): the attempt's folder
  // by the paper's name, as the head names it; and kept from a rename or
  // delete, named or not, from the moment the machine says its exam (not
  // once exam/state answers): the attempt's folder, its record and all of
  // its .exam (which gone, the workbench no longer knows the deadline, nor
  // the agent's evidence its hook), and every question's folder, as
  // workbench/projects lists them and as exam/state names their workspaces
  const roomName = room?.title || room?.exam;
  const attemptDir = info?.exam;
  const treeNames = attemptDir && roomName ? { [attemptDir]: roomName } : undefined;
  const questionDirs = new Set([
    ...projects.filter((p) => p.exam === attemptDir).map((p) => p.path),
    ...(room?.questions ?? []).filter((q) => q.workspace).map((q) => `${attemptDir}/${q.workspace}`),
  ]);
  const treeKept = attemptDir
    ? (path: string) =>
        path === attemptDir ||
        path === `${attemptDir}/exam-attempt.edn` ||
        path === `${attemptDir}/.exam` ||
        path.startsWith(`${attemptDir}/.exam/`) ||
        questionDirs.has(path)
    : undefined;
  useEffect(() => {
    if (!ready || !info?.exam) return;
    void loadExam();
    const id = setInterval(() => void loadExam(), 30_000);
    return () => clearInterval(id);
  }, [ready, info?.exam, loadExam]);
  const counting = room?.begun === true && room.over !== true;
  useEffect(() => {
    if (!counting) return;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [counting]);
  const left = room?.begun ? examLeft(room, skew, tick) : null;
  // past the deadline the machine hands in what is left, then says it is
  // over: read again until it does, or the relay closes the sitting
  const timeUp = left === 0 && room?.over !== true;
  // the agent takes nothing once the sitting is over, nor past an end the
  // relay set sooner than the machine's, which the machine does not know
  // (byj-1wv.118.19): a question is still handed in within the grace
  const promptOff = room?.over === true || (room?.endedEarly === true && left === 0);
  useEffect(() => {
    if (!timeUp) return;
    const id = setInterval(() => void loadExam(), 5000);
    return () => clearInterval(id);
  }, [timeUp, loadExam]);
  const paperAgents = (room?.agents ?? []).filter((a) => agents[a] !== undefined);
  const roomAgent = room?.agent ?? (lobbyAgent || paperAgents[0] || "byj");
  const roomQ =
    room?.questions?.find((q) => q.id === examQ) ?? room?.questions?.find((q) => `${room.assignment}/${q.id}` === project) ?? room?.questions?.[0];
  // the thread's folder follows the question shown, a practical one's own,
  // a choice keeping the last practical one's: a browser that kept none (a
  // new one, mid-exam) is never left on the work tree, outside every question
  const roomBegun = room?.begun === true;
  const roomQId = roomQ?.id;
  useEffect(() => {
    if (!roomBegun || !room || !roomQ) return;
    const folder = (q: ExamQuestion) => `${room.assignment}/${q.id}`;
    const practical = (room.questions ?? []).filter((q) => q.mode !== "choice");
    if (roomQ.mode !== "choice") {
      if (folder(roomQ) !== project) keepProject(folder(roomQ));
    } else if (!practical.some((q) => folder(q) === project) && practical[0]) {
      keepProject(folder(practical[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomBegun, roomQId, project]);

  /** A question shown: a practical one's folder the thread's, the tree's and the terminal's. */
  const pickQuestion = (q: ExamQuestion, st: ExamState | null = room) => {
    keepExamQ(q.id);
    if (st && q.mode !== "choice") keepProject(`${st.assignment}/${q.id}`);
  };

  const examState = (st: ExamState) => {
    setExamSt(st);
    setSkew(examSkew(st));
    setRefresh((n) => n + 1);
  };

  const prepareExam = async () => {
    setExamBusy("prepare");
    try {
      examState(await wb.current!.request<ExamState>("exam/prepare", { agent: roomAgent }));
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setExamBusy(undefined);
    }
  };

  /** The agent started in the exam's folder, before any clock: one not signed in says so here. */
  const checkAgent = async () => {
    const c = wb.current;
    if (!c || !info || !room) return;
    setExamBusy("check");
    setAgentCheck(undefined);
    try {
      await initialize(c, roomAgent);
      await c.request("session/new", { cwd: `${info.cwd}/${room.assignment}`, mcpServers: [] }, roomAgent);
      setAgentCheck("ok");
    } catch (err) {
      setAgentCheck(errText(err));
    } finally {
      setExamBusy(undefined);
    }
  };

  const beginExam = async () => {
    if (!room) return;
    const min = room["time-limit-min"];
    if (!(await confirm(min ? t("room:beginConfirm", { min }) : t("room:beginConfirmNoLimit"), { action: t("room:begin") }))) return;
    setExamBusy("begin");
    try {
      const st = await wb.current!.request<ExamState>("exam/begin", { agent: roomAgent });
      examState(st);
      const first = st.questions?.[0];
      if (first) pickQuestion(first, st);
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setExamBusy(undefined);
    }
  };

  const answerExam = (q: ExamQuestion, answers: number[]) => {
    setExamSt((st) => (st ? { ...st, questions: st.questions?.map((x) => (x.id === q.id ? { ...x, answer: answers } : x)) } : st));
    wb.current?.request("exam/answer", { question: q.id, answers }).then(
      () => void loadExam(),
      (err: unknown) => {
        setProblem(errText(err));
        void loadExam();
      },
    );
  };

  /** What is open and not saved under `folder`, saved: a hand-in takes the files as they are on the machine. */
  const saveUnder = async (folder: string) => {
    for (const f of filesRef.current) if (dirty(f) && f.path.startsWith(folder + "/")) await save(f.path);
  };

  const submitExam = async (q?: ExamQuestion) => {
    if (!room) return;
    if (q) {
      const handInsLeft = Math.max(0, (room.attempts ?? 1) - q["handed-in"]);
      const label = t("room:questionN", { n: (room.questions ?? []).indexOf(q) + 1 });
      if (!(await confirm(t("room:submitConfirm", { q: label, left: handInsLeft }), { action: t("room:submitQuestion") }))) return;
    } else {
      // finishing ends the sitting there (byj-1wv.113), what is left handed in first
      const n = room.pending?.length ?? 0;
      if (!(await confirm(n === 0 ? t("room:finishAllIn") : t("room:finishConfirm", { count: n }), { action: t("room:finish") }))) return;
    }
    setExamBusy("submit");
    try {
      await saveUnder(q ? `${room.assignment}/${q.id}` : room.assignment);
      const r = await wb.current!.request<{ receipt?: string }>("exam/submit", q ? { question: q.id } : { all: true, finish: true });
      if (r.receipt) {
        // one hand-in a question, as read from what was printed; all of it to the one asked for when it reads as none
        const at = Date.now();
        const read = parseReceipt(r.receipt);
        const next = { ...receipts };
        if (read) for (const one of read) next[one.question] = { text: r.receipt, at };
        else if (q) next[q.id] = { text: r.receipt, at };
        keepReceipts(next);
      }
    } catch (err) {
      setProblem(errText(err));
    } finally {
      setExamBusy(undefined);
      void loadExam();
    }
  };

  /** ACP's initialize, once an agent on this connection: no client fs or terminal, since the agents act on the machine themselves. */
  const initialize = useCallback(async (c: WorkbenchConnection, name: string) => {
    if (initialized.current.has(name)) return;
    await c.request("initialize", { protocolVersion: 1, clientCapabilities: { fs: { readTextFile: false, writeTextFile: false }, terminal: false } }, name);
    initialized.current.add(name);
  }, []);

  /** Shows a thread: one the agent has open, sent again; a saved one, loaded. Taken only once it opens. */
  const openThread = useCallback(async (x: PastThread, cwd: string, name: string) => {
    const c = wb.current;
    if (!c) return;
    dispatch({ type: "switch", sessionId: x.sessionId });
    try {
      if (x.state === "open") {
        const r = await c.request<{ busy?: boolean }>("workbench/replay", { agent: name, sessionId: x.sessionId });
        dispatch({ type: "busy", busy: r.busy === true });
      } else {
        await c.request("session/load", { sessionId: x.sessionId, cwd, mcpServers: [] }, name);
      }
    } catch (err) {
      dispatch({ type: "reset" });
      if (!(err instanceof RpcError && err.code === Codes.closed)) setProblem(t("workbench:threadNotOpened", { m: errText(err) }));
    }
  }, []);

  const history = useCallback(async () => {
    const c = wb.current;
    if (!c || !info) return [];
    return pastThreads(c, cwdOf(info, project), agentName);
  }, [info, project, agentName]);

  const goBack = useCallback(
    (x: PastThread) => {
      if (info) void openThread(x, cwdOf(info, project), agentName);
    },
    [info, project, openThread, agentName],
  );

  const prompt = useCallback(
    (text: string) => {
      const c = wb.current;
      if (!c || !info) return false;
      dispatch({ type: "prompt", text });
      (async () => {
        try {
          let sid = threadRef.current.sessionId;
          if (!sid) {
            await initialize(c, agentName);
            const s = await c.request<{ sessionId: string }>("session/new", { cwd: cwdOf(info, project), mcpServers: [] }, agentName);
            sid = s.sessionId;
            dispatch({ type: "session", sessionId: sid });
            // an exam's question on the paper's model, as the terminal's claude --model sits it
            if (exam?.model && agentName === "claude") {
              await c.request("session/set_config_option", { sessionId: sid, configId: "model", value: exam.model }, agentName).catch(() => {});
            }
          }
          const r = await c.request<{ stopReason?: string }>("session/prompt", { sessionId: sid, prompt: [{ type: "text", text }] }, agentName);
          dispatch({ type: "done", stopReason: r.stopReason });
        } catch (err) {
          dispatch({ type: "failed", message: errText(err) });
        }
      })();
      return true;
    },
    [info, project, agentName, exam],
  );

  const cancel = () => {
    if (thread.sessionId) wb.current?.notify("session/cancel", { sessionId: thread.sessionId }, agentName);
  };

  const choose = (p: PermissionItem, optionId: string) => {
    if (wb.current?.respond(p.rpc, { outcome: { outcome: "selected", optionId } })) dispatch({ type: "chosen", rpc: p.rpc, optionId });
  };

  // the files

  const list = useCallback(async (path: string) => {
    const c = wb.current;
    if (!c) throw new Error(t("workbench:notConnected"));
    const r = await c.request<{ entries: Entry[] }>("fs/list", { path });
    return r.entries;
  }, []);

  const openFile = useCallback(async (path: string) => {
    setProblem(null);
    if (filesRef.current.some((f) => f.path === path)) {
      setActive(path);
      setPane("editor");
      return;
    }
    const c = wb.current;
    if (!c) return;
    try {
      const r = await c.request<FileText>("fs/read", { path });
      setFiles((fs) => (fs.some((f) => f.path === path) ? fs : [...fs, { path, text: r.text, saved: r.text, version: r.version, loadedAt: String(Date.now()) }]));
      setActive(path);
      setPane("editor");
    } catch (err) {
      if (err instanceof RpcError && err.code === Codes.binary) setProblem(t("workbench:notText", { path }));
      else if (err instanceof RpcError && err.code === Codes.tooLarge) setProblem(t("workbench:tooLarge", { path }));
      else setProblem(errText(err));
    }
  }, []);

  const onChange = useCallback((path: string, text: string) => {
    setFiles((fs) => fs.map((f) => (f.path === path ? { ...f, text } : f)));
  }, []);

  const save = useCallback(async (path: string, overwrite = false) => {
    const c = wb.current;
    const f = filesRef.current.find((x) => x.path === path);
    if (!c || !f) return;
    try {
      const r = await c.request<{ version: string }>("fs/write", overwrite ? { path, text: f.text } : { path, text: f.text, version: f.gone ? "" : f.version });
      setFiles((fs) => fs.map((x) => (x.path === path ? { ...x, saved: f.text, version: r.version, conflict: false, gone: false } : x)));
      setRefresh((n) => n + 1);
    } catch (err) {
      if (err instanceof RpcError && err.code === Codes.conflict) setFiles((fs) => fs.map((x) => (x.path === path ? { ...x, conflict: true } : x)));
      else setProblem(errText(err));
    }
  }, []);

  const reload = async (path: string) => {
    const c = wb.current;
    if (!c) return;
    try {
      const r = await c.request<FileText>("fs/read", { path });
      setFiles((fs) => fs.map((x) => (x.path === path ? { ...x, text: r.text, saved: r.text, version: r.version, conflict: false, gone: false, loadedAt: String(Date.now()) } : x)));
    } catch (err) {
      setProblem(errText(err));
    }
  };

  const close = async (path: string) => {
    const f = filesRef.current.find((x) => x.path === path);
    if (f && dirty(f) && !(await confirm(t("workbench:closeUnsaved", { path }), { action: t("workbench:closeAnyway"), danger: true }))) return;
    setFiles((fs) => fs.filter((x) => x.path !== path));
    setActive((a) => (a === path ? filesRef.current.find((x) => x.path !== path)?.path : a));
  };

  const join = (folder: string, name: string) => (folder ? `${folder}/${name}` : name);

  const create = async (folder: string, kind: "file" | "dir") => {
    const name = await ask(kind === "file" ? t("workbench:newFile") : t("workbench:newFolder"), "", t("workbench:nameLabel"));
    if (!name) return;
    const path = join(folder, name);
    try {
      if (kind === "file") {
        await wb.current?.request("fs/write", { path, text: "", version: "" });
        setRefresh((n) => n + 1);
        await openFile(path);
      } else {
        await wb.current?.request("fs/mkdir", { path });
        setRefresh((n) => n + 1);
      }
    } catch (err) {
      setProblem(errText(err));
    }
  };

  const rename = async (e: Entry) => {
    const name = await ask(t("workbench:renameHeading", { name: e.name }), e.name, t("workbench:nameLabel"));
    if (!name || name === e.name) return;
    const folder = e.path.includes("/") ? e.path.slice(0, e.path.lastIndexOf("/")) : "";
    const to = join(folder, name);
    try {
      await wb.current?.request("fs/rename", { path: e.path, to });
      const moved = (p: string) => (p === e.path ? to : p.startsWith(e.path + "/") ? to + p.slice(e.path.length) : p);
      setFiles((fs) => fs.map((x) => ({ ...x, path: moved(x.path) })));
      setActive((a) => (a ? moved(a) : a));
      setRefresh((n) => n + 1);
    } catch (err) {
      setProblem(errText(err));
    }
  };

  const remove = async (e: Entry) => {
    if (!(await confirm(t("workbench:deleteConfirm", { path: e.path }), { action: t("workbench:delete"), danger: true }))) return;
    try {
      await wb.current?.request("fs/delete", { path: e.path });
      const under = (p: string) => p === e.path || p.startsWith(e.path + "/");
      setFiles((fs) => fs.filter((x) => !under(x.path)));
      setActive((a) => (a && under(a) ? undefined : a));
      setRefresh((n) => n + 1);
    } catch (err) {
      setProblem(errText(err));
    }
  };

  const current = useMemo(() => files.find((f) => f.path === active), [files, active]);
  const back = `/s/${encodeURIComponent(share.id)}`;

  const ended = conn.kind === "closed" ? conn.end : undefined;
  // the layout's toggles where there are panes to show: not the lobby's, not a refusal's
  const panesShown = !(ended === "forbidden" || ended === "gone") && !(room && !room.begun);
  const screen =
    ended === undefined ? (
      <Suspense fallback={<p className="muted">{t("common:loading")}</p>}>
        <Screen shareId={share.id} input />
      </Suspense>
    ) : (
      <p className="muted screen-ended">{t("session:screenEnded")}</p>
    );
  // a pane, as the tabpanel of its tab while the panes are tabs
  const panel = (id: Pane) => (narrow ? { role: "tabpanel", id: panelId("wb", id), "aria-labelledby": tabId("wb", id) } : {});
  const unsaved = files.some(dirty);
  const asking = thread.items.some((x) => x.kind === "permission" && x.chosen === undefined);
  const name = agentLabel(agentName);
  /** The agent's prompt, in view and focused: its pane picked, or its side unfolded. */
  const askAgent = () => {
    if (narrow) setPane("agent");
    else foldAgent(false);
    setTimeout(() => promptField.current?.focus(), 0);
  };
  const editor = (
    <div className="wb-editor" {...panel("editor")}>
      {files.length > 0 && (
        <div className="wb-tabs" role="tablist" aria-label={t("workbench:openFiles")}>
          {files.map((f) => (
            <span key={f.path} className={`wb-tab${f.path === active ? " wb-tab-active" : ""}`}>
              <button role="tab" aria-selected={f.path === active} onClick={() => setActive(f.path)} title={f.path}>
                <FileIcon name={f.path} />
                {f.path.split("/").pop()}
                {dirty(f) && (
                  <span className="wb-dirty" aria-label={t("workbench:unsaved")}>
                    ●
                  </span>
                )}
              </button>
              <button className="wb-tab-close" aria-label={t("workbench:closeFile", { path: f.path })} onClick={() => void close(f.path)}>
                ×
              </button>
            </span>
          ))}
          <span className="grow" />
          {current && (
            // the call to act only when there is something to save
            <button className={`small${dirty(current) ? " primary" : ""}`} disabled={disabled || !dirty(current)} onClick={() => void save(current.path)}>
              {t("workbench:save")}
            </button>
          )}
        </div>
      )}
      {current?.conflict && (
        <p className="banner warn small">
          {t("workbench:conflict", { path: current.path })}{" "}
          <button className="small" onClick={() => void reload(current.path)}>
            {t("workbench:reload")}
          </button>{" "}
          <button className="small" onClick={() => void save(current.path, true)}>
            {t("workbench:keepMine")}
          </button>
        </p>
      )}
      {current?.gone && <p className="banner warn small">{t("workbench:gone", { path: current.path })}</p>}
      {current ? (
        <Suspense fallback={<p className="muted">{t("common:loading")}</p>}>
          <CodeEditor
            path={current.path}
            text={current.text}
            loadedAt={current.loadedAt}
            onChange={onChange}
            onSave={() => void save(current.path)}
            label={t("workbench:editorLabel", { path: current.path })}
          />
        </Suspense>
      ) : (
        // nothing open: the ways to something to work on, here
        <div className="wb-empty">
          <p className="muted">{narrow ? t("workbench:noFileOpenNarrow") : t("workbench:noFileOpen")}</p>
          <div className="row">
            {narrow ? (
              <button className="small" onClick={() => setPane("files")}>
                {t("workbench:browseFiles")}
              </button>
            ) : (
              filesFolded && (
                <button className="small" onClick={() => foldFiles(false)}>
                  {t("workbench:showFiles")}
                </button>
              )
            )}
            <button className="small" disabled={disabled} onClick={() => void create("", "file")}>
              {t("workbench:newFile")}
            </button>
            <button className="small" onClick={askAgent}>
              {t("workbench:askAgent", { name })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
  const paneTabs = (
    <Tabs<Pane>
      prefix="wb"
      label={t("workbench:panes")}
      value={pane}
      onChange={setPane}
      tabs={[
        { id: "files", label: t("workbench:files") },
        {
          id: "editor",
          label: (
            <>
              {t("workbench:editor")}
              {unsaved && <Mark className="wb-dirty" text={t("workbench:unsaved")} glyph="●" />}
            </>
          ),
        },
        { id: "terminal", label: t("workbench:terminal") },
        {
          id: "agent",
          label: (
            <>
              {name}
              {/* what the agent wants of the person is seen from any pane */}
              {asking ? (
                <Mark className="wb-pane-ask" text={t("workbench:paneAsking", { name })} glyph="!" />
              ) : (
                thread.busy && <Mark className="wb-pane-busy" text={t("workbench:working", { name })} glyph="●" />
              )}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <div className={`session workbench${narrow ? " workbench-narrow" : ""}`} data-pane={narrow ? pane : undefined}>
      {askDialog}
      {confirmDialog}
      <header className="session-head">
        <a
          className="back"
          href={back}
          onClick={(e) => {
            e.preventDefault();
            navigate(back);
          }}
          aria-label={t("workbench:backToShare")}
        >
          ←
        </a>
        <div className="wb-head-title">
          {/* an exam's machine is its room, named by the exam once, here: not the share's title and machine id */}
          {(() => {
            const heading = room ? t("room:roomTitle", { title: room.title || room.exam || room.assignment }) : t("workbench:title", { title: share.title || share.id });
            // cut short when the head is full: the whole of it on hover
            return <h1 title={heading}>{heading}</h1>;
          })()}
          {/* under it, the work tree, said once — or, in an exam's room begun, when the sitting runs */}
          {room?.begun && examSpan(room) ? (
            <span className="wb-head-root wb-head-span">{examSpan(room)}</span>
          ) : (
            info && (
              <span className="wb-head-root" title={info.root}>
                {info.root}
              </span>
            )
          )}
        </div>
        <span className="grow" />
        {/* the room's status where a person looks for how it stands: the head's end, before the layout */}
        {room?.begun && <ExamBar st={room} left={left} busy={examBusy === "submit"} onFinish={() => void submitExam()} />}
        {!narrow && panesShown && (
          // which panes are shown, as an editor's layout toggles: a narrow screen has its tabs instead
          <div className="wb-layout" role="group" aria-label={t("workbench:layout")}>
            <LayoutToggle shown={!filesFolded} label={t("workbench:showFiles")} onClick={() => foldFiles(!filesFolded)} at="start" />
            <LayoutToggle shown={!terminalHidden} label={t("workbench:showTerminal")} onClick={() => hideTerminal(!terminalHidden)} at="bottom" />
            <LayoutToggle shown={!agentFolded} label={t("workbench:showAgent", { name })} onClick={() => foldAgent(!agentFolded)} at="end" />
          </div>
        )}
        <StateChip conn={conn} sidecar={sidecar} />
      </header>
      {ended === "taken" && (
        <p className="banner">
          {t("workbench:taken")}{" "}
          <button className="small" onClick={() => setAttempt((n) => n + 1)}>
            {t("workbench:useHere")}
          </button>
        </p>
      )}
      {ended === "forbidden" && <p className="banner">{t("workbench:forbidden")}</p>}
      {ended === "gone" && <p className="banner">{t("workbench:gone404")}</p>}
      {conn.kind === "open" && !sidecar && <p className="banner muted">{t("workbench:waitingMachine")}</p>}
      {problem && (
        <p className="banner error">
          {problem}{" "}
          <button className="small ghost" onClick={() => setProblem(null)} aria-label={t("common:close")}>
            ×
          </button>
        </p>
      )}
      {room?.begun && <ExamNotice st={room} left={left} returnURL={returnURL} />}
      {ended === "forbidden" || ended === "gone" ? null : room && !room.begun ? (
        <ExamLobby
          st={room}
          agents={paperAgents}
          agent={roomAgent}
          onAgent={(a) => {
            setLobbyAgent(a);
            setAgentCheck(undefined);
          }}
          machine={ready}
          agentCheck={agentCheck}
          busy={examBusy === "submit" ? undefined : examBusy}
          onPrepare={() => void prepareExam()}
          onCheckAgent={() => void checkAgent()}
          onBegin={() => void beginExam()}
          terminal={screen}
          closesAt={closesAt}
        />
      ) : (
        <>
          {narrow && paneTabs}
          <SplitPane
            storageKey="share.workbench.side"
            sideTitle={t("workbench:thread")}
            className="wb-split-agent"
            open={narrow}
            collapsed={agentFolded}
            onCollapsed={foldAgent}
            keepSide
            main={
              <div className="wb-main">
                {/* the files at the start, as wide as dragged, folded from their edge or the head */}
                <SplitPane
                  storageKey="share.workbench.files"
                  sideTitle={t("workbench:files")}
                  className="wb-split-files"
                  at="start"
                  bounds={{ min: 160, max: 480, initial: 240 }}
                  open={narrow}
                  collapsed={filesFolded}
                  onCollapsed={foldFiles}
                  keepSide
                  side={
                    <div className="wb-pane wb-pane-files" {...panel("files")}>
                      {info ? (
                        <FileTree
                          root={info.root}
                          list={list}
                          refresh={refresh}
                          open={active}
                          onOpen={(p) => void openFile(p)}
                          onNew={(folder, kind) => void create(folder, kind)}
                          onRename={(e) => void rename(e)}
                          onDelete={(e) => void remove(e)}
                          onWorkHere={(e) => {
                            keepProject(e.path);
                            setPane("agent");
                          }}
                          onRefresh={() => setRefresh((n) => n + 1)}
                          names={treeNames}
                          kept={treeKept}
                        />
                      ) : (
                        <nav className="wb-files muted">{t("common:loading")}</nav>
                      )}
                    </div>
                  }
                  main={
                    <div className="wb-center">
                      {room?.begun && roomQ && (
                        // the paper's questions as numbers over the one shown, so
                        // however many there are the files keep their column
                        <ExamQuestionPanel
                          st={room}
                          q={roomQ}
                          busy={examBusy === "submit"}
                          receipt={receipts[roomQ.id]}
                          onAnswer={(a) => answerExam(roomQ, a)}
                          onSubmit={() => void submitExam(roomQ)}
                          nav={
                            <ExamQuestions
                              st={room}
                              current={roomQ.id}
                              onPick={(q) => {
                                pickQuestion(q);
                                setPane("editor");
                              }}
                            />
                          }
                        />
                      )}
                      <VSplit
                        storageKey="share.workbench.terminal"
                        label={t("workbench:splitResize")}
                        topLabel={t("workbench:editor")}
                        bottomLabel={t("workbench:terminal")}
                        top={editor}
                        // hidden from the head, not taken away: its connection stays (narrow, the tabs choose)
                        only={!narrow && terminalHidden ? "top" : undefined}
                        bottom={
                          <div className="wb-pane wb-pane-terminal" {...panel("terminal")}>
                            {screen}
                          </div>
                        }
                      />
                    </div>
                  }
                />
              </div>
            }
            side={
              <div className="wb-pane wb-pane-agent" {...panel("agent")}>
                <AgentThread
                  inputRef={promptField}
                  inRoom={roomBegun}
                  context={roomBegun && room && roomQ ? t("room:questionN", { n: (room.questions ?? []).indexOf(roomQ) + 1 }) : undefined}
                  thread={thread}
                  agent={agents[agentName] ?? { state: "idle" }}
                  agentName={agentName}
                  agentNames={Object.keys(agents)}
                  agentLocked={agentLocked}
                  onAgent={(name: string) => {
                    keepAgent(name);
                    dispatch({ type: "reset" });
                  }}
                  disabled={disabled || !info || !restored || promptOff}
                  why={promptWhy(conn, sidecar, promptOff, !info || !restored, agentLabel(agentName))}
                  onPrompt={prompt}
                  onCancel={cancel}
                  onChoose={choose}
                  onNewThread={() => dispatch({ type: "reset" })}
                  root={info?.root ?? ""}
                  project={project}
                  projects={projects}
                  onProject={(p) => keepProject(p)}
                  history={history}
                  onGoBack={goBack}
                  timeline={<ShareTimeline share={share} me={me} socket={socket} />}
                />
              </div>
            }
          />
        </>
      )}
    </div>
  );
}

/** A head toggle showing or hiding one pane, drawn as the window with that edge filled. */
function LayoutToggle({ shown, label, onClick, at }: { shown: boolean; label: string; onClick: () => void; at: "start" | "bottom" | "end" }) {
  const edge = at === "start" ? "M2 2.5h4v11H2z" : at === "end" ? "M10 2.5h4v11h-4z" : "M2 9.5h12v4H2z";
  return (
    <button type="button" className="small ghost wb-layout-toggle" aria-pressed={shown} aria-label={label} title={label} onClick={onClick}>
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <rect x="1.5" y="2" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <path d={edge} fill="currentColor" fillOpacity={shown ? 0.9 : 0.15} />
      </svg>
    </button>
  );
}

/** A pane tab's mark: a glyph for the eye, its words for a screen reader. */
function Mark({ className, text, glyph }: { className: string; text: string; glyph: string }) {
  return (
    <span className={`wb-pane-mark ${className}`} title={text}>
      <span aria-hidden="true">{glyph}</span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

/**
 * The threads of a folder to go back to, newest first: those the workbench's
 * agent has open, then the saved ones not among them — one another process
 * holds (a terminal's byj) marked so, since it cannot be opened here.
 */
async function pastThreads(c: WorkbenchConnection, cwd: string, agent: string): Promise<PastThread[]> {
  const [open, saved] = await Promise.all([
    c
      .request<{ sessions?: { agent?: string; sessionId: string; cwd: string; title?: string; busy?: boolean; updatedAt?: string }[] }>("workbench/sessions")
      .catch(() => ({ sessions: [] })),
    c
      .request<{ sessions?: { sessionId: string; cwd?: string; title?: string; updatedAt?: string; _meta?: { brainyard?: { turnCount?: number; heldBy?: string } } }[] }>(
        "session/list",
        { cwd },
        agent,
      )
      .catch(() => ({ sessions: [] })),
  ]);
  const here = (open.sessions ?? []).filter((x) => x.cwd === cwd && (x.agent ?? "byj") === agent);
  const ids = new Set(here.map((x) => x.sessionId));
  const savedOnes = (saved.sessions ?? []).filter((x) => (x.cwd === undefined || x.cwd === cwd) && !ids.has(x.sessionId));
  const titles = new Map((saved.sessions ?? []).map((x) => [x.sessionId, x.title ?? ""]));
  const turns = new Map((saved.sessions ?? []).map((x) => [x.sessionId, x._meta?.brainyard?.turnCount]));
  return [
    ...here.map((x): PastThread => ({ sessionId: x.sessionId, title: x.title || titles.get(x.sessionId) || "", updatedAt: x.updatedAt, turns: turns.get(x.sessionId), state: "open", busy: x.busy })),
    ...savedOnes.map((x): PastThread => ({
      sessionId: x.sessionId,
      title: x.title ?? "",
      updatedAt: x.updatedAt,
      turns: x._meta?.brainyard?.turnCount,
      state: x._meta?.brainyard?.heldBy ? "elsewhere" : "saved",
    })),
  ];
}

/** The thread's folder as a path in the machine: the work tree's, or a folder in it. */
function cwdOf(info: WorkbenchInfo, project: string): string {
  return project ? `${info.cwd}/${project}` : info.cwd;
}

/**
 * Why the agent's prompt takes nothing, said in it (byj-1wv.51.24): no
 * reconnection promised to a page closed for good or taken by another tab,
 * whose banner says why; connecting at first, not "not connected".
 */
function promptWhy(conn: WorkbenchState, sidecar: boolean, over: boolean, loading: boolean, name: string): string | undefined {
  if (over) return t("workbench:whyOver");
  switch (conn.kind) {
    case "closed":
      return undefined;
    case "connecting":
      return t("session:connecting");
    case "waiting":
      return t("workbench:whyOffline");
    case "open":
      if (!sidecar) return t("workbench:whyOffline");
      return loading ? t("workbench:whyLoading", { name }) : undefined;
  }
}

function StateChip({ conn, sidecar }: { conn: WorkbenchState; sidecar: boolean }) {
  switch (conn.kind) {
    case "open":
      return sidecar ? <span className="chip chip-live">{t("workbench:connected")}</span> : <span className="chip chip-warn">{t("workbench:machineAway")}</span>;
    case "connecting":
      return <span className="chip">{t("session:connecting")}</span>;
    case "waiting":
      return <span className="chip chip-warn">{t("session:reconnecting", { s: Math.ceil(conn.retryInMs / 1000) })}</span>;
    case "closed":
      return null;
  }
}
